/**
 * The type of remote control messages.
 */
export const REMOTE_CONTROL_MESSAGE_NAME = 'remote-control';
/**
 * The value for the "var" attribute of feature tag in disco-info packets.
 */
export const DISCO_REMOTE_CONTROL_FEATURE = 'http://jitsi.org/meet/remotecontrol';
/**
 * The remote control event.
 *
 * @typedef {object} RemoteControlEvent
 * @property {EVENTS | REQUESTS} type - The type of the message.
 * @property {number} x - Avaibale for type === mousemove only. The new x
 * coordinate of the mouse.
 * @property {number} y - For mousemove type - the new y
 * coordinate of the mouse and for mousescroll - represents the vertical
 * scrolling diff value.
 * @property {number} button - 1(left), 2(middle) or 3 (right). Supported by
 * mousedown, mouseup and mousedblclick types.
 * @property {KEYS} key - Represents the key related to the event. Supported by
 * keydown and keyup types.
 * @property {KEYS[]} modifiers - Represents the modifier related to the event.
 * Supported by keydown and keyup types.
 * @property {PERMISSIONS_ACTIONS} action - Supported by type === permissions.
 * Represents the action related to the permissions event.
 */
/**
 * Optional properties. Supported for permissions event for action === request.
 *
 * @property {string} userId - The user id of the participant that has sent the
 * request.
 * @property {string} userJID - The full JID in the MUC of the user that has
 * sent the request.
 * @property {string} displayName - The displayName of the participant that has
 * sent the request.
 * @property {boolean} screenSharing - True if the SS is started for the local
 * participant and false if not.
 */
/**
 * Types of remote-control events.
 *
  * @readonly
  * @enum {string}
 */
export const EVENTS = {
    mousemove: 'mousemove',
    mousedown: 'mousedown',
    mouseup: 'mouseup',
    mousedblclick: 'mousedblclick',
    mousescroll: 'mousescroll',
    keydown: 'keydown',
    keyup: 'keyup',
    // Kamo additions. A chord and a run of text each travel as one message
    // rather than as a stream of key events, so that a message lost on the way
    // cannot strand a modifier held down on someone else's keyboard.
    keycombo: 'keycombo',
    typetext: 'typetext',
    permissions: 'permissions',
    start: 'start',
    stop: 'stop',
    supported: 'supported'
};
/**
 * Types of remote-control requests.
 *
  * @readonly
  * @enum {string}
 */
export const REQUESTS = {
    start: 'start'
};
/**
 * Actions for the remote control permission events.
 *
 * @readonly
 * @enum {string}
 */
export const PERMISSIONS_ACTIONS = {
    request: 'request',
    grant: 'grant',
    deny: 'deny',
    error: 'error',
    // Kamo addition: the request could not be considered, because the machine
    // has no control helper installed. Telling the asker that it was "rejected"
    // would be untrue and would stop them asking again once it is sorted out.
    // A client that predates this falls through to the generic error message.
    unavailable: 'unavailable'
};
/**
 * Requests the KamoMeet control agent understands in addition to Jitsi's own.
 *
 * `start` is shared with upstream but carries a display rather than an Electron
 * desktopCapturer source id; the rest are ours.
 */
export const AGENT_REQUESTS = {
    hello: 'hello',
    pair: 'pair',
    ping: 'ping',
    start: 'start'
};
/**
 * The loopback ports the control agent may be listening on, in the order it
 * tries to bind them. The page walks the same list, so the two never have to
 * agree out of band.
 *
 * The list is short on purpose: a browser logs a console error for every
 * WebSocket that fails to connect, and this whole list is walked on every join
 * by anyone who has not installed the agent. Five is ample headroom for a
 * loopback port already being taken.
 */
export const AGENT_PORTS = [31771, 31772, 31773, 31774, 31775];
/**
 * Chords the controller can send as one atomic action, because sending them as
 * separate key events across a lossy data channel is not reliable.
 *
 * Ctrl+Alt+Del is deliberately absent: Windows will not deliver a synthesised
 * one, and offering a button that cannot work is worse than not offering it.
 */
export const KEY_COMBOS = [
    { id: 'altTab', key: 'tab', modifiers: ['alt'] },
    { id: 'commandTab', key: 'tab', modifiers: ['command'] },
    { id: 'escape', key: 'escape', modifiers: [] },
    { id: 'lockScreen', key: 'l', modifiers: ['command'] },
    { id: 'printScreen', key: 'printscreen', modifiers: [] },
    { id: 'showDesktop', key: 'd', modifiers: ['command'] }
];
/**
 * The states the "request control" menu item can be in for a participant.
 *
 * These live here rather than beside the button because two different menus —
 * the thumbnail's and the participants pane's — both have to work them out, and
 * a selector in this feature is the one place that should know how.
 */
export const REMOTE_CONTROL_MENU_STATES = {
    NOT_SUPPORTED: 0,
    NOT_STARTED: 1,
    REQUESTING: 2,
    STARTED: 3
};
